import { api } from '../api/client';

import { API_FAVORITES_URL, API_OVERVIEW_DASHBOARD_URL, API_OVERVIEW_URL } from '../utils';

/**
 * The dashboard a collection's Overview shows (keepr-api docs/SCHEMA.md
 * "Charts" › "Overview and Home"; plan docs/plans/analytics.md §4.6, §6.6;
 * charts A7, ruled q04). ONE dashboard: the one a manager chose for everyone,
 * or the one a person chose for themselves — the cascade person → collection
 * → none, every tier re-checked by the server on every read, a choice that no
 * longer stands falling through without a word. A sub-collection never shows
 * a dashboard of the collection above it (owner ruling 2026-10-21, KPR-1084);
 * the API's `from` is always null and is not read.
 */

/** Where the dashboard Overview shows came from — what its SourceMark says. */
export const OVERVIEW_SOURCE = Object.freeze({ PERSON: 'person', COLLECTION: 'collection' });

const EMPTY = Object.freeze({ dashboard: null, source: null, mine: null, collectionChoice: null });

/**
 * GET /api/collections/{id}/overview → { dashboard, source, mine,
 * collectionChoice }: `dashboard` as the dashboard page reads it here (tiles
 * judged) or null; `source` 'person' | 'collection' | null; `mine` /
 * `collectionChoice` the
 * caller's own and the collection's own choice, when they stand. An API a
 * deploy behind (404 on the route itself, or none at all) reads as nothing
 * chosen — the page goes without the section.
 */
export async function getOverview(collectionId) {
	try {
		const { data } = await api.get(API_OVERVIEW_URL(collectionId));
		return {
			dashboard: data && data.dashboard && typeof data.dashboard === 'object' ? data.dashboard : null,
			source: Object.values(OVERVIEW_SOURCE).includes(data?.source) ? data.source : null,
			mine: typeof data?.mine === 'string' ? data.mine : null,
			collectionChoice: typeof data?.collectionChoice === 'string' ? data.collectionChoice : null,
		};
	} catch (err) {
		if (err?.response?.status === 404) return { ...EMPTY };
		throw err;
	}
}

/** "Show on Overview for everyone" (a manager's): `{ _id, overviewDashboard_id }`. */
export async function setCollectionOverview(collectionId, dashboardId) {
	const { data } = await api.put(API_OVERVIEW_DASHBOARD_URL(collectionId), { dashboard_id: dashboardId });
	return data;
}

/** The collection shows no dashboard of its own (a manager's). */
export async function clearCollectionOverview(collectionId) {
	const { data } = await api.delete(API_OVERVIEW_DASHBOARD_URL(collectionId));
	return data;
}

/** "Show on my Overview": `{ ok, _id, dashboard_id }` — one per collection; a second choice replaces the first. */
export async function setMyOverview(collectionId, dashboardId) {
	const { data } = await api.put(`${API_FAVORITES_URL}/collection/${collectionId}/overview-dashboard/${dashboardId}`);
	return data;
}

/** "Use the collection's Overview": the caller's own choice goes. */
export async function clearMyOverview(collectionId) {
	const { data } = await api.delete(`${API_FAVORITES_URL}/collection/${collectionId}/overview-dashboard`);
	return data;
}
